import { createSlice, nanoid } from "@reduxjs/toolkit";

const MAX_ACTIVE = 3;

/**
 * Calculate display duration from message content when not explicitly provided.
 * Base 2 s + ~40 ms per character, clamped to [2 000, 6 000] ms.
 * Exported so callers can align related timing (e.g. de-duplication windows) with the toast.
 */
export function calcDurationMs(payload) {
    if (payload.durationMs != null) return payload.durationMs;
    const textLen = (payload.title?.length || 0) + (payload.message?.length || 0);
    return Math.max(2000, Math.min(2000 + textLen * 40, 6000));
}

const initialState = {
    active: [], // toasts currently visible (max MAX_ACTIVE)
    queue: [],  // toasts waiting for a slot
};

const toastSlice = createSlice({
    name: "toast",
    initialState,
    reducers: {
        showToast: {
            reducer(state, action) {
                if (state.active.length < MAX_ACTIVE) {
                    state.active.push(action.payload);
                } else {
                    state.queue.push(action.payload);
                }
            },
            prepare(payload) {
                return {
                    payload: {
                        id: payload.id || nanoid(),
                        variant: payload.variant || "info",
                        title: payload.title || "",
                        message: payload.message || "",
                        durationMs: calcDurationMs(payload),
                    },
                };
            },
        },

        // Called after a toast's exit animation completes.
        // Removes the toast from active and promotes the next queued item.
        dismissToast(state, action) {
            state.active = state.active.filter((t) => t.id !== action.payload);
            if (state.queue.length > 0 && state.active.length < MAX_ACTIVE) {
                state.active.push(state.queue.shift());
            }
        },

        clearToasts(state) {
            state.active = [];
            state.queue = [];
        },
    },
});

export const { showToast, dismissToast, clearToasts } = toastSlice.actions;
export default toastSlice.reducer;